import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type {
  CreateSiteRequest,
  DashboardSiteStatus,
  Site,
  SiteType,
  UpdateSiteRequest,
} from '@mvcp/contracts';
import {
  createSite,
  deactivateSite,
  getDashboard,
  getSites,
  reactivateSite,
  updateSite,
} from '../api';
import { MAX_TEXT_LEN } from '../lib/field-limits';
// One owner for the band vocabulary, pill and unknown-state labels (Story 8.1).
import { BandPill, UNKNOWN_LABEL } from './bands';
import type { DrillTarget } from './CockpitShell';
import { ConfirmModal } from './ConfirmModal';
import { FormModal } from './FormModal';
import { SiteDetail } from './SiteDetail';

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/**
 * Option lists are inlined here (with the contract as source of truth) because
 * the web consumes `@mvcp/contracts` as TYPES ONLY — importing the runtime
 * `FLAG_STATES` / `VESSEL_TYPES` arrays breaks `vite build` (CJS __exportStar
 * cannot be resolved by rollup; see Story 1.6 debug log). Keep in sync with
 * `packages/contracts/src/entities.ts`.
 */
// Exported so `enum-sync.test.ts` asserts they stay byte-equal to the contract
// runtime arrays (the copy has no compile-time guard; the test is the guard).
export const FLAG_STATE_OPTIONS = [
  'Panama', 'Liberia', 'Marshall Islands', 'Hong Kong', 'Singapore', 'Malta',
  'Bahamas', 'Greece', 'Cyprus', 'China', 'Japan', 'Norway', 'Denmark', 'Italy',
  'United Kingdom', 'Germany', 'Netherlands',
] as const;
export const VESSEL_TYPE_OPTIONS = [
  'Crude Oil Tanker', 'LNG Carrier', 'Bulk Carrier', 'Container', 'Chemical Tanker',
] as const;

/** IMO number format: a 7-digit numeric string (mirrors the API guard). */
const IMO_RE = /^\d{7}$/;

/**
 * An `<option>` for a STORED value the closed list does not contain — or nothing, when
 * there is no such value or the list already offers it.
 *
 * Existing rows are NOT guaranteed to hold a listed value: `seed-demo.ts` stored `'GR'` and
 * `'Container Ship'` (the contract's are `'Greece'` and `'Container'`), and
 * `import.repository.ts` writes these columns unvalidated. A `<select>` whose `value`
 * matches no option renders BLANK while React state still holds the stored string — so the
 * form would misstate the vessel, and the first "fix" of the apparently-empty select would
 * silently overwrite a value the Manager was never shown. Rendering it as its own option
 * states the record instead. It is never coerced or auto-corrected: while unchanged it
 * stays out of the patch entirely.
 *
 * Keyed on the STORED value, deliberately, not on the current form value. Keyed on the
 * latter the option would vanish the moment the Manager picked `'Greece'`, stranding them
 * with no way back to `'GR'` short of cancelling the whole dialog — a one-way door out of a
 * value the row still holds. Anchoring it to the row keeps the original selectable for as
 * long as the row holds it.
 */
function preservedOption(
  storedValue: string | undefined,
  options: readonly string[],
): React.JSX.Element | null {
  const value = storedValue?.trim();
  if (!value || options.includes(value)) return null;
  return <option value={value}>{value}</option>;
}

/** Rows beyond this count are windowed until the user asks to see all (AD-11). */
const PAGE_SIZE = 50;

/**
 * The LIFECYCLE pill for a mothballed Site (DEV-4424 d) — rendered beside the name, never
 * in the Status column.
 *
 * Deliberately the literal `status-pill` markup rather than `StatusPill`/`BandPill`: both
 * of those are keyed to a `ComplianceStatus`/`HealthBand`, and "deactivated" is not a
 * compliance verdict — it is the reason this Site HAS no compliance verdict. Widening
 * either component to carry it would put a lifecycle word into the vocabulary every
 * compliance surface shares. The `UsersView` roster-status pill is the precedent.
 *
 * Neutral tone, because nothing is being claimed about the vessel's compliance: its
 * coverage was filtered out of the engine entirely, so its Status cell reads `Not
 * assessed` — and that is an honest answer only while this pill says why.
 */
function LifecyclePill(): React.JSX.Element {
  return (
    <span className="status-pill" data-tone="neutral">
      <span className="status-pill__dot" aria-hidden="true" />
      Deactivated
    </span>
  );
}

/** Which lifecycle transition the confirm dialog is offering, and for which Site. */
type LifecycleAction = { kind: 'deactivate' | 'reactivate'; site: Site };

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; sites: Site[] }
  | { kind: 'error'; message: string };

type Filter = 'all' | 'ship' | 'office';

/**
 * The per-Site compliance the Status column renders (Story 8.1). Loaded from
 * `GET /dashboard`, whose `sites[]` is `{siteId, band, worstDriver}` — the SAME rows
 * the dashboard's Site-status list and (per Story 7.2) the Site detail header show,
 * so the list cannot disagree with either. Independent of the `/sites` read: a
 * compliance failure must never blank the fleet list.
 */
type ComplianceState =
  | { kind: 'loading' }
  | { kind: 'ready'; bySiteId: Map<string, DashboardSiteStatus> }
  | { kind: 'error' };

/**
 * The Status cell for one row.
 *
 * An in-flight or failed read, and a Site absent from the dashboard payload, are all
 * states in which the band is UNKNOWN — a different claim from `neutral`/"Not
 * assessed" ("we assessed this vessel and found nothing to score"). Rendering the
 * neutral label there would state, in grey, that a genuinely `bad` vessel is
 * unassessed. So the tone stays neutral (nothing is being claimed) while the TEXT
 * says plainly that the state is unknown — the `SiteHealthPill` precedent from
 * Story 7.2, applied to the list.
 */
function SiteStatusCell({
  siteId,
  state,
  deactivated,
}: {
  siteId: string;
  state: ComplianceState;
  deactivated: boolean;
}): React.JSX.Element {
  // A mothballed Site (DEV-4424 d) is absent from the dashboard payload BY DESIGN — the
  // server drops it from `sites[]` exactly as it drops it from the scores. Falling through
  // to the `!row` arm below would therefore label every deactivated vessel "Compliance
  // unavailable", i.e. claim a read FAILED when nothing failed; per the docblock above,
  // that label is reserved for a genuinely unknown state. There is no band to state here
  // and no error to report, so the cell states nothing and the neutral "Deactivated" pill
  // on the name cell carries the whole explanation — one clear claim per row, not two.
  if (deactivated) {
    return <span aria-label="Not scored while deactivated">—</span>;
  }
  if (state.kind === 'loading') {
    return <BandPill band="neutral" label={UNKNOWN_LABEL.loading} />;
  }
  const row = state.kind === 'ready' ? state.bySiteId.get(siteId) : undefined;
  if (!row) return <BandPill band="neutral" label={UNKNOWN_LABEL.error} />;
  return (
    <>
      <BandPill band={row.band} />
      {/* UX-DR13: a red state is never unexplained. `worstDriver` rides in the same
          payload row as the band, and both the dashboard's Site-status list and the
          Site detail header render it — so the list would be the one surface showing
          "At Risk" with no why. Same muted caption idiom as ComplianceDashboard. */}
      {row.worstDriver && (
        <div className="muted" style={{ fontSize: 13 }}>{row.worstDriver}</div>
      )}
    </>
  );
}

/**
 * Compliance Manager cockpit → Sites (Story 2.1, FR-4). A type-branching create
 * form (ship: IMO + flag state + vessel type; office: city) plus a type-filterable
 * list with live counts. Each Site's Status cell carries that Site's REAL compliance
 * band, read from `GET /dashboard` (Story 8.1) — the list and the Site detail page
 * can never state different compliance for the same vessel.
 *
 * Manager-only in practice: `CockpitShell` is this view's only mount, and `App.tsx`
 * routes a System Administrator to `AdminShell`, which has no Sites section. So the
 * `GET /dashboard` role guard is a belt on a path no Admin can reach here.
 *
 * `companyMaritime` (Story 3.2, FR-15) gates the maritime-only fields: a
 * non-maritime Company's form offers NO "ship" type option and renders none of the
 * IMO / flag-state / vessel-type inputs (hidden, not merely empty) — only Office.
 */
export function SitesView({
  companyMaritime,
  focusId = null,
  onOpenRecord,
  onBack,
}: {
  companyMaritime: boolean;
  /**
   * Cockpit drill (Story 4.4): when set, the pane opens the Site detail for this id
   * (driven by the cockpit back stack) instead of the list. Absent standalone.
   */
  focusId?: string | null;
  /** Drill to a spine neighbor from a Site's detail (Story 4.4). */
  onOpenRecord?: (target: DrillTarget) => void;
  /** Pop the cockpit back stack (Story 4.4) — the drilled Site detail's Back. */
  onBack?: () => void;
}): React.JSX.Element {
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [complianceState, setComplianceState] = useState<ComplianceState>({ kind: 'loading' });
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  /**
   * Whether deactivated Sites are shown (DEV-4424 d). Off by default — the fleet a Manager
   * is answerable for is the ACTIVE one.
   *
   * The toggle is CLIENT-side: `load()` asks for the whole roster once and this filters it,
   * so flipping it cannot change the type-filter counts' denominator asynchronously, cannot
   * strand the `PAGE_SIZE` window mid-list, and costs no round trip. It is the visibility
   * predicate below and the `counts` memo that must agree, and they agree because both read
   * the SAME `inScope` list.
   */
  const [showDeactivated, setShowDeactivated] = useState(false);
  // Standalone-mode selection (no router). In cockpit mode `focusId` drives the
  // detail instead and this stays null (row clicks route through `onOpenRecord`).
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const cockpit = onOpenRecord != null;
  // Sequence number for the compliance read, so a superseded response cannot publish.
  const complianceRead = useRef(0);

  // Create-form state. Fail-closed default `office` (the ship UI is gated on
  // `companyMaritime`, so it never renders until the flag resolves true). Once the
  // flag resolves, the default type tracks it — ship for a maritime Company, office
  // otherwise — unless the user has explicitly picked a type (see `typeChosen`).
  const [type, setType] = useState<SiteType>('office');
  const typeChosen = useRef(false);
  const [name, setName] = useState('');
  const [imo, setImo] = useState('');
  const [flagState, setFlagState] = useState('');
  const [vesselType, setVesselType] = useState('');
  const [city, setCity] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /** Whether the create dialog is open (Story: create moved out of the page body). */
  const [adding, setAdding] = useState(false);
  /**
   * The Site currently being EDITED, or null when the form is in its add disposition
   * (DEV-4424). One dialog, two dispositions — the `FrameworksView` precedent.
   *
   * It holds the whole `Site`, not just the id, because the stored row IS the baseline the
   * sparse patch diffs against: `SiteRepository.update` is per-column `COALESCE`, so an
   * omitted key means "leave alone" and re-sending an unchanged value would be
   * indistinguishable noise. Keeping the snapshot here (rather than re-reading
   * `sites.find(...)` at submit time) also means a list refresh mid-dialog cannot move the
   * baseline under an open form.
   */
  const [editing, setEditing] = useState<Site | null>(null);
  /**
   * The lifecycle transition awaiting confirmation, or null when no confirm is open
   * (DEV-4424 d). Holds the whole `Site` so the dialog copy can name the vessel even if a
   * refresh reorders the list underneath it.
   */
  const [lifecycle, setLifecycle] = useState<LifecycleAction | null>(null);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  /**
   * The Site whose save just succeeded, parked until the refreshed list has rendered so
   * focus can be placed deliberately (see the effect below). `null` when nothing is pending.
   */
  const focusAfterSave = useRef<string | null>(null);

  const load = useCallback(async () => {
    // Only fall back to the skeleton from a NON-ready state (first mount, or after an
    // error). A refresh keeps the rows it already has on screen.
    //
    // Unconditionally entering `loading` made the Site DETAIL branch — which needs a
    // `ready` state to find `activeSite` — unmount to a bare "Loading…" pane on every
    // post-save reload, remount, and refetch every sub-resource (vendors, devices,
    // removals, compliance). Editing from the detail header is exactly that path, so the
    // one gesture this change adds was also the one that flashed the page away.
    setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }));
    try {
      // The WHOLE roster, active and deactivated alike (DEV-4424 d). This screen is the one
      // place a mothballed Site has to be reachable — to be listed under the toggle, and to
      // keep its detail page opening — so it is also the one caller that opts in. Every
      // other `getSites()` caller keeps the active-only default.
      const sites = await getSites(true);
      setState({ kind: 'ready', sites });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  // Deliberately a SEPARATE read from `load()`: the compliance column must fail on
  // its own. A 403 or a 500 leaves every row saying "Compliance unavailable" while
  // the fleet list itself still renders.
  const loadCompliance = useCallback(async () => {
    // A superseded read must not publish: it carries a verdict from before the
    // mutation (or the detail visit) that replaced it, so last-response-wins would
    // let an older payload overwrite a newer one — e.g. two Sites created in quick
    // succession leaving the second at "Compliance unavailable". The `SiteDetail`
    // `complianceRead` idiom (Story 7.2), applied to the list's read.
    const read = (complianceRead.current += 1);
    const stale = (): boolean => read !== complianceRead.current;
    setComplianceState({ kind: 'loading' });
    try {
      const summary = await getDashboard();
      if (stale()) return;
      setComplianceState({
        kind: 'ready',
        bySiteId: new Map(summary.sites.map((s) => [s.siteId, s])),
      });
    } catch {
      if (stale()) return;
      setComplianceState({ kind: 'error' });
    }
  }, []);

  useEffect(() => {
    void load();
    void loadCompliance();
  }, [load, loadCompliance]);

  // Track the resolved maritime flag: default a maritime Company's form to `ship`
  // and a non-maritime one to `office` (the latter also guarantees the hidden ship
  // fields can never be the active branch — fail closed). A user's explicit type
  // choice wins and is never overridden.
  useEffect(() => {
    if (!typeChosen.current) setType(companyMaritime ? 'ship' : 'office');
  }, [companyMaritime]);

  const sites = state.kind === 'ready' ? state.sites : [];
  /**
   * The rows the "Show deactivated" toggle admits (DEV-4424 d) — the base for BOTH the
   * type-filter counts and the visibility predicate, so the chips can never promise rows
   * the table does not show. "Active" is the absence of the timestamp, never `s.status`
   * (that is the compliance rollup pill and says nothing about the Site's lifecycle).
   */
  const inScope = useMemo(
    () => (showDeactivated ? sites : sites.filter((s) => !s.deactivatedAt)),
    [sites, showDeactivated],
  );
  const counts = useMemo(
    () => ({
      all: inScope.length,
      ship: inScope.filter((s) => s.type === 'ship').length,
      office: inScope.filter((s) => s.type === 'office').length,
    }),
    [inScope],
  );
  // Page-scoped search (Story 4.4): case-insensitive substring over the Site name,
  // combined with the type-filter chips (mirrors VendorDirectory/DeviceDirectory).
  const query = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      inScope.filter((s) => {
        const byType = filter === 'all' || s.type === filter;
        const byName = query === '' || s.name.toLowerCase().includes(query);
        return byType && byName;
      }),
    [inScope, filter, query],
  );
  const visible = showAll ? filtered : filtered.slice(0, PAGE_SIZE);

  // "Show deactivated" is one of the filters Clear clears: it is a narrowing the Manager
  // turned on, and a Clear that left the fleet list showing retired vessels would be the
  // one control on this toolbar that does not do what it says.
  const hasActiveFilter = filter !== 'all' || query !== '' || showDeactivated;
  function clearFilters(): void {
    setFilter('all');
    setSearch('');
    setShowDeactivated(false);
    setShowAll(false);
  }

  // Open a Site's detail: drill through the cockpit back stack when wired, else the
  // standalone local selection (Epic-2 behavior).
  function openSite(id: string): void {
    if (onOpenRecord) onOpenRecord({ kind: 'site', id });
    else setSelectedSiteId(id);
  }

  // The Site whose detail is open: `focusId` in cockpit mode, else the standalone
  // local selection.
  const activeSiteId = cockpit ? focusId : selectedSiteId;
  const activeSite = activeSiteId ? sites.find((s) => s.id === activeSiteId) ?? null : null;

  // Re-read compliance whenever a Site detail CLOSES, keyed on the TRANSITION itself
  // rather than on any one control's handler. A certificate submitted or removed in
  // there moves that vessel's band, and a list still showing the pre-edit word is
  // exactly the "two answers for one vessel" this story closes.
  //
  // Why the transition and not the Back button's `onClick`: Back is only ONE of the
  // exits. Clicking the sidebar's own "Sites" item while a detail is open also closes
  // it — `ShellLayout` fires `onNavSelect` even for the already-active section and
  // `CockpitShell` clears the drill stack, while `activeIndex` stays on Sites, so THIS
  // instance survives with its `complianceState` and simply re-renders the list.
  // Re-reading from the handler alone left that exit showing the pre-edit band.
  //
  // It also costs nothing on the drill-from-Dashboard path: there the stack pops to
  // `preDrillIndex` (Dashboard), so this view unmounts and the mount effect above
  // re-reads on the way back in, instead of firing a discarded fleet aggregation.
  const detailWasOpen = useRef(false);
  useEffect(() => {
    const open = activeSiteId != null;
    if (detailWasOpen.current && !open) void loadCompliance();
    detailWasOpen.current = open;
  }, [activeSiteId, loadCompliance]);

  /**
   * Put focus somewhere deliberate once a saved edit's refreshed list has rendered.
   *
   * `useDialogA11y` restores focus to the control that OPENED the dialog, which is the right
   * default — but a rename can move that control out from under it: `SiteRepository.list` is
   * `ORDER BY name`, so the row re-sorts, and it can also leave the current type filter or
   * search, or be pushed past `PAGE_SIZE` by the window. When that happens the opener is
   * detached and focus silently falls to `<body>`, stranding a keyboard user at the top of
   * the document with no announcement.
   *
   * So: the row's own Edit control when it is still on screen (matching what the restore
   * would have done), else the Fleet heading — the heading of the region that just changed,
   * which is the standard place to land after a context shift. Runs on the READY state
   * rather than inside the save, because the row must exist before it can be focused.
   */
  useEffect(() => {
    const id = focusAfterSave.current;
    if (id === null || state.kind !== 'ready') return;
    focusAfterSave.current = null;
    const row = document.querySelector<HTMLElement>(`[data-edit-site="${id}"]`);
    (row ?? document.getElementById('sites-heading'))?.focus();
  }, [state]);

  function resetForm(): void {
    setName('');
    setImo('');
    setFlagState('');
    setVesselType('');
    setCity('');
  }

  const isEditing = editing !== null;

  /**
   * The type the form is currently working in: the edited Site's IMMUTABLE type while
   * editing, else the create form's own (user-chosen or maritime-default) selection.
   *
   * Deliberately derived instead of pushing the Site's type into `type`: editing must not
   * overwrite the create form's pending choice, and `type` is never sent in a patch
   * (`UpdateSiteRequest` excludes it at the type level).
   */
  const formType: SiteType = editing ? editing.type : type;
  /**
   * Whether the ship-only fields are EXPOSED. Validation reads the same gate the inputs
   * render behind, so a non-maritime Company — which edits a ship's name alone — is never
   * blocked on an input it cannot see (FR-15 is not lifted for editing).
   */
  const shipFieldsShown = companyMaritime && formType === 'ship';

  /**
   * Open the shared dialog in its EDIT disposition, prefilled from the stored Site.
   *
   * Prefilled TRIMMED, matching how `onSubmit` trims before comparing and sending. A
   * `<select>` in particular can only hold a value that equals one of its options, so a
   * padded stored value compared raw would register as an edit the moment the dialog opened
   * and rewrite a row nobody touched. The padding itself is never "corrected" behind the
   * Manager's back — an untouched field simply stays out of the patch.
   */
  function startEdit(site: Site): void {
    setEditing(site);
    setAdding(false);
    // ONE dialog at a time (the house rule below): opening the form retires any pending
    // lifecycle confirm rather than stacking a second `useDialogA11y` focus trap on it.
    closeLifecycle();
    setFormError(null);
    setName(site.name.trim());
    setImo(site.imo?.trim() ?? '');
    setFlagState(site.flagState?.trim() ?? '');
    setVesselType(site.vesselType?.trim() ?? '');
    setCity(site.city?.trim() ?? '');
  }

  /**
   * Leave the edit disposition, discarding whatever was typed — so reopening Edit shows the
   * STORED values, never the abandoned ones. Also leaves the ADD disposition, so the one
   * success path at the end of `onSubmit` closes the dialog whichever disposition opened it
   * (the `FrameworksView.cancelEdit` contract).
   */
  function cancelEdit(): void {
    setEditing(null);
    setAdding(false);
    setFormError(null);
    resetForm();
  }

  /** Dismiss the lifecycle confirm, discarding any failure message it was showing. */
  function closeLifecycle(): void {
    setLifecycle(null);
    setLifecycleError(null);
  }

  /**
   * Ask for confirmation before a lifecycle transition (DEV-4424 d).
   *
   * Deactivation is not destructive — no row is deleted or detached — but it silently
   * changes what every compliance number on the platform MEANS, so it is confirmed rather
   * than fired from the row. The dialog states those consequences (see `lifecycleDialog`);
   * there is deliberately no typed reason, unlike the `UsersView` deactivate, because the
   * transition's trace is the Site's activity feed rather than an audit reason.
   */
  function askLifecycle(kind: LifecycleAction['kind'], site: Site): void {
    // One dialog at a time, from the other side: a confirm opened while the form dialog is
    // up would be the nested modal the house rule forbids.
    cancelEdit();
    setLifecycleError(null);
    setLifecycle({ kind, site });
  }

  /**
   * Run the confirmed transition, then refresh — the `onSubmit` post-mutation idiom.
   *
   * Compliance is re-read beside the list because that is the WHOLE POINT of the gesture:
   * deactivating pulls this Site's vendors and devices out of the fleet coverage sets, so
   * every band in the Status column is stale the instant the 204 lands. Refreshing the
   * roster alone would leave the list stating the pre-deactivation verdict for vessels the
   * Manager never touched.
   *
   * The dialog closes on SUCCESS only: a 409 (already in that state) or any other failure
   * keeps it open with the server's sentence visible, because the page behind the scrim is
   * not.
   */
  async function onConfirmLifecycle(): Promise<void> {
    if (!lifecycle) return;
    setLifecycleError(null);
    setLifecycleBusy(true);
    try {
      if (lifecycle.kind === 'deactivate') await deactivateSite(lifecycle.site.id);
      else await reactivateSite(lifecycle.site.id);
      // Hand the focus effect a target BEFORE the refresh, exactly as a save does.
      // `ConfirmModal` restores focus to its opener on close, and for a DEACTIVATE that
      // opener is the row's own button — which the very transition just removed from the
      // default list. Restoring onto a detached node is a silent no-op and focus drops to
      // `<body>`: the keyboard user loses their place entirely, which is the failure the
      // rename path already has a dedicated test against. The effect lands on the row when
      // it is still on screen (a reactivate, or a deactivate with the toggle on) and on
      // the Fleet heading when it is not.
      focusAfterSave.current = lifecycle.site.id;
      closeLifecycle();
      await Promise.all([load(), loadCompliance()]);
    } catch (err: unknown) {
      setLifecycleError(
        err instanceof Error ? err.message : "Couldn't update this site. Try again.",
      );
    } finally {
      setLifecycleBusy(false);
    }
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setFormError(null);

    const trimmedName = name.trim();
    const trimmedImo = imo.trim();
    const trimmedCity = city.trim();

    /**
     * Which fields the user ACTUALLY edited — the one predicate that drives both the
     * validation below and the patch built from it, so "what we check" and "what we send"
     * can never disagree.
     *
     * In the ADD disposition there is no stored row, so every field counts as edited and
     * the create-time required-field rules apply to the whole record exactly as they always
     * have. In EDIT a field counts only when it DIFFERS from the stored value, because a
     * stored row was never held to those rules: `seed-demo.ts` creates the demo offices
     * with no city at all, and `import.repository.ts` writes `imo`/`flag_state`/
     * `vessel_type`/`city` nullable. Validating an untouched blank made exactly those rows
     * uneditable — a plain rename died on "City is required for offices." before any
     * request. A field the user DID touch still validates normally, so clearing a real city
     * is still refused.
     */
    // Both sides TRIMMED. The form value is trimmed before it is compared or sent, so the
    // stored value has to be trimmed for the comparison too — otherwise a row holding
    // `'Piraeus '` reads as edited the instant the dialog opens, and an unrelated save
    // silently rewrites it (for a ship, a padded IMO would also block a plain rename on the
    // 7-digit rule). The importer trims on the way in (`import.controller.ts:120,126,132`),
    // so this is defence in depth — but "only what the user touched ships" is the contract
    // this suite states, and whitespace must not be able to break it.
    const stored = {
      name: editing?.name.trim() ?? '',
      imo: editing?.imo?.trim() ?? '',
      flagState: editing?.flagState?.trim() ?? '',
      vesselType: editing?.vesselType?.trim() ?? '',
      city: editing?.city?.trim() ?? '',
    };
    const edited = {
      name: !editing || trimmedName !== stored.name,
      imo: !editing || trimmedImo !== stored.imo,
      flagState: !editing || flagState !== stored.flagState,
      vesselType: !editing || vesselType !== stored.vesselType,
      city: !editing || trimmedCity !== stored.city,
    };

    // Explicit onSubmit validation (the form uses noValidate): branch by type so
    // ship-only fields are never required for offices and vice-versa. ONE validation
    // path for both dispositions — add and edit can never drift into two forms that
    // accept different values; they differ only in WHICH fields the user has touched.
    if (edited.name && !trimmedName) {
      setFormError('Site name is required.');
      return;
    }
    if (formType === 'ship') {
      if (shipFieldsShown) {
        if (edited.imo && !IMO_RE.test(trimmedImo)) {
          setFormError('IMO number must be a 7-digit number.');
          return;
        }
        if (edited.flagState && !flagState) {
          setFormError('Select a flag state.');
          return;
        }
        if (edited.vesselType && !vesselType) {
          setFormError('Select a vessel type.');
          return;
        }
      }
    } else if (edited.city && !trimmedCity) {
      setFormError('City is required for offices.');
      return;
    }

    // The sparse patch (edit only): ONLY the fields the user actually changed. `update()`
    // writes `name = COALESCE($2, name)` and friends, so an omitted key means "leave
    // alone" — and an empty diff is a no-op the server would answer 200 to, which is why
    // it is caught here as an advisory instead of a silent success.
    //
    // A stored value the closed option list does not contain (`'GR'`, `'Container Ship'`)
    // is `edited: false` and so never ships: it is preserved, not coerced and not
    // rewritten behind the Manager's back.
    const patch: UpdateSiteRequest = {};
    if (editing) {
      if (edited.name) patch.name = trimmedName;
      if (formType === 'ship') {
        if (edited.imo) patch.imo = trimmedImo;
        if (edited.flagState) patch.flagState = flagState as CreateSiteRequest['flagState'];
        if (edited.vesselType) patch.vesselType = vesselType as CreateSiteRequest['vesselType'];
      } else if (edited.city) {
        patch.city = trimmedCity;
      }
      if (Object.keys(patch).length === 0) {
        setFormError('No changes to save.');
        return;
      }
    }

    setSubmitting(true);
    try {
      if (editing) {
        await updateSite(editing.id, patch);
        // Where focus should land once the refreshed list renders (see the effect above).
        focusAfterSave.current = editing.id;
      } else if (formType === 'ship' && shipFieldsShown) {
        // FAIL CLOSED on the create path. The ship-field validation above is gated on
        // `shipFieldsShown` while this branch keys off `formType`; if those two ever
        // disagree, an unvalidated ship create would post three empty strings cast straight
        // through the contract types. Only the maritime-default `useEffect` keeps that
        // unreachable today, and that is exactly the kind of guarantee that quietly stops
        // holding — so the branch states the condition itself rather than inheriting it.
        const input: CreateSiteRequest = {
          name: trimmedName,
          type: 'ship',
          imo: trimmedImo,
          flagState: flagState as CreateSiteRequest['flagState'],
          vesselType: vesselType as CreateSiteRequest['vesselType'],
        };
        await createSite(input);
      } else if (formType === 'ship') {
        // A ship type with its fields hidden: no validated values exist to send, so this
        // refuses rather than inventing them. Unreachable while the effect holds.
        setFormError('Ships cannot be added for this company.');
        return;
      } else {
        const input: CreateSiteRequest = { name: trimmedName, type: 'office', city: trimmedCity };
        await createSite(input);
      }
      // Close on SUCCESS only, in whichever disposition opened the dialog. A failure keeps
      // it open with its error, so the typed values survive and the message is visible —
      // the page behind the scrim is not (the ConfirmModal rule, applied to these forms).
      cancelEdit();
      // Re-read compliance too: a Site the dashboard payload predates would
      // otherwise sit at "Compliance unavailable" instead of its real (neutral)
      // band, which is the false-unknown mirror of the false-reassurance bug.
      await Promise.all([load(), loadCompliance()]);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const chip = (key: Filter, label: string): React.JSX.Element => {
    const isActive = filter === key;
    return (
      <button
        type="button"
        onClick={() => {
          setFilter(key);
          setShowAll(false);
        }}
        aria-pressed={isActive}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '5px 12px',
          borderRadius: 'var(--rounded-full)',
          border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
          background: isActive ? 'var(--accent-soft)' : 'transparent',
          color: isActive ? 'var(--accent-on-soft)' : 'var(--text-2)',
          fontFamily: 'var(--font-sans)',
          fontWeight: 700,
          fontSize: 13,
          cursor: 'pointer',
        }}
      >
        {label}
        <span data-numeric style={{ opacity: 0.8 }}>
          {counts[key]}
        </span>
      </button>
    );
  };

  /**
   * ONE dialog for both dispositions (DEV-4424): `startEdit` prefills the same form, so add
   * and edit cannot drift into two forms that validate differently — and never a SECOND or
   * a NESTED `FormModal` (the house rule at `SiteDetail.tsx:285`: two stacked
   * `useDialogA11y` focus traps on one screen).
   *
   * Held in a const rather than inlined because the Site DETAIL branch below returns early,
   * and editing is reachable from the detail header too — so both returns render this same
   * element. Only one of those returns ever runs, so only one dialog is ever mounted.
   */
  const siteDialog =
    adding || isEditing ? (
      <FormModal
        title={isEditing ? 'Edit Site' : 'Add a Site'}
        titleId="site-form-heading"
        // Esc, the scrim and the X all land here — and all three are REFUSED while the
        // request is in flight. Dismissing mid-save left the outcome invisible: the PATCH
        // was still running, and a rejection then wrote `formError` onto a form nobody
        // could see, so the Manager was told nothing and believed the edit had saved. The
        // submit button is already `disabled={submitting}` and Cancel likewise; this closes
        // the third door.
        onClose={() => {
          if (!submitting) cancelEdit();
        }}
      >
        <form
          noValidate
          onSubmit={onSubmit}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}
        >
          <label className="stack" style={{ gap: 4, flex: '0 1 130px' }}>
            <span className="field-label">Type</span>
            <select
              aria-label="Site type"
              value={formType}
              // `type` is immutable after creation (`UpdateSiteRequest` omits it), so the
              // control states the stored value and refuses the change rather than offering
              // one the API would reject.
              disabled={isEditing}
              onChange={(e) => {
                typeChosen.current = true;
                setType(e.target.value as SiteType);
                setFormError(null);
              }}
              style={fieldStyle}
            >
              {/* Ship is a maritime-only Site type (Story 3.2, FR-15): a
                  non-maritime Company's form omits it entirely — except when it is
                  the edited Site's own (disabled) type, where hiding it would leave
                  the select naming the wrong type. */}
              {(companyMaritime || (isEditing && formType === 'ship')) && (
                <option value="ship">Ship</option>
              )}
              <option value="office">Office</option>
            </select>
          </label>

          <label className="stack" style={{ gap: 4, flex: '1 1 200px' }}>
            <span className="field-label">Name</span>
            <input
              aria-label="Site name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_TEXT_LEN}
              style={fieldStyle}
            />
          </label>

          {shipFieldsShown && (
            <>
              <label className="stack" style={{ gap: 4, flex: '0 1 140px' }}>
                <span className="field-label">IMO number</span>
                <input
                  aria-label="IMO number"
                  inputMode="numeric"
                  value={imo}
                  onChange={(e) => setImo(e.target.value)}
                  placeholder="e.g. 9512345"
                  style={{ ...fieldStyle, fontFamily: 'var(--font-mono)' }}
                />
              </label>
              <label className="stack" style={{ gap: 4, flex: '0 1 170px' }}>
                <span className="field-label">Flag state</span>
                <select
                  aria-label="Flag state"
                  value={flagState}
                  onChange={(e) => setFlagState(e.target.value)}
                  style={fieldStyle}
                >
                  <option value="">Select…</option>
                  {preservedOption(editing?.flagState, FLAG_STATE_OPTIONS)}
                  {FLAG_STATE_OPTIONS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </label>
              <label className="stack" style={{ gap: 4, flex: '0 1 180px' }}>
                <span className="field-label">Vessel type</span>
                <select
                  aria-label="Vessel type"
                  value={vesselType}
                  onChange={(e) => setVesselType(e.target.value)}
                  style={fieldStyle}
                >
                  <option value="">Select…</option>
                  {preservedOption(editing?.vesselType, VESSEL_TYPE_OPTIONS)}
                  {VESSEL_TYPE_OPTIONS.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}

          {formType === 'office' && (
            <label className="stack" style={{ gap: 4, flex: '1 1 160px' }}>
              <span className="field-label">City</span>
              <input
                aria-label="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                maxLength={MAX_TEXT_LEN}
                style={fieldStyle}
              />
            </label>
          )}

          {/* The trigger is "Add a Site" / "Edit <name>" while the submit stays
              "Add Site" / "Save Site", so a by-name query (or a screen reader) can
              never confuse the two. */}
          <button type="submit" className="btn-accent" data-surface="accent" disabled={submitting}>
            {submitting ? (isEditing ? 'Saving…' : 'Adding…') : isEditing ? 'Save Site' : 'Add Site'}
          </button>
          {isEditing && (
            <button type="button" className="icon-button" onClick={cancelEdit} disabled={submitting}>
              Cancel
            </button>
          )}
        </form>
        <div role="alert" aria-live="assertive" style={{ minHeight: 16 }}>
          {formError && (
            <span className="status-pill" data-tone="bad">
              <span className="status-pill__dot" aria-hidden="true" />
              {formError}
            </span>
          )}
        </div>
      </FormModal>
    ) : null;

  /**
   * The lifecycle confirm (DEV-4424 d) — a `ConfirmModal`, never a second `FormModal` and
   * never one nested in the other: this view already owns `siteDialog`, and `askLifecycle`
   * / `startEdit` each retire the other's dialog, so at most ONE is ever mounted.
   *
   * The copy names what DROPS OUT of compliance, because that is the part a Manager cannot
   * see from the row: the vessel disappearing from the list is obvious, the Company's
   * framework scores silently recomputing is not. It also states what is KEPT — the
   * assignments, installs and certificates — since the whole affordance is only safe to
   * press if the Manager knows it is reversible.
   *
   * Like `siteDialog` this is held in a const rather than inlined, because the Site DETAIL
   * branch below returns early and both lifecycle controls are reachable from the detail
   * header too.
   */
  const lifecycleDialog = lifecycle ? (
    <ConfirmModal
      title={lifecycle.kind === 'deactivate' ? 'Deactivate this site?' : 'Reactivate this site?'}
      message={
        lifecycle.kind === 'deactivate' ? (
          <>
            <strong>{lifecycle.site.name}</strong> will stop counting towards your compliance:
            its vendors and devices leave your <strong>framework scores</strong>, your{' '}
            <strong>Needs Attention</strong> list and every <strong>new report</strong>, and
            the site leaves this list unless you show deactivated sites.
            <br />
            Nothing is deleted — its vendors, devices and certificates are kept, it stays
            readable, and reactivating restores it exactly as it is now. While it is
            deactivated it cannot be edited and no vendor or device can be added or removed.
          </>
        ) : (
          <>
            <strong>{lifecycle.site.name}</strong> returns to your fleet with the vendors,
            devices and certificates it had — so your framework scores, Needs Attention list
            and reports all count it again, and it can be edited once more.
          </>
        )
      }
      // Distinct from the row/header TRIGGER ("Deactivate MV Aurora"), per the same
      // "Add a Site" trigger vs "Add Site" submit split this view already uses — a by-name
      // query, or a screen reader, can never confuse the two.
      confirmLabel={lifecycle.kind === 'deactivate' ? 'Deactivate site' : 'Reactivate site'}
      // Reactivate is RESTORATIVE — it is the recovery from the destructive half of this
      // pair, and removes nothing. The modal's default red is right for "Deactivate site"
      // and wrong for its undo: telling a user to hesitate over the safe action is how a
      // reversible pair gets read as irreversible.
      confirmTone={lifecycle.kind === 'deactivate' ? 'bad' : 'ok'}
      busy={lifecycleBusy}
      error={lifecycleError}
      onConfirm={() => void onConfirmLifecycle()}
      onCancel={() => {
        // Esc, the scrim and Cancel all land here, and all three are refused mid-request:
        // dismissing then would leave the outcome — including a 409 — reported nowhere.
        if (lifecycleBusy) return;
        closeLifecycle();
      }}
    />
  ) : null;

  // ---- Full-page Site detail (in-pane, NOT a drawer/router) ----
  // When a detail id is active (cockpit `focusId` or a standalone selection) the pane
  // NEVER falls through to the list/create-form: it mirrors Vendor/Device — Loading +
  // Back while the list loads, error + Back + Retry on failure, not-found + Back when
  // the id is absent from a loaded list, else the detail (Story 4.4).
  // Closing the detail re-reads compliance — but that is wired to the open/closed
  // TRANSITION (see the effect beside `activeSiteId`), not to this handler, because
  // the sidebar's "Sites" item closes the detail without going through here.
  const closeDetail = cockpit ? () => onBack?.() : () => setSelectedSiteId(null);
  const backControl = (
    <button type="button" className="icon-button" onClick={closeDetail} style={{ alignSelf: 'flex-start' }}>
      {/* Cockpit Back returns to the drill origin (dynamic); standalone returns to the list. */}
      {cockpit ? '← Back' : '← Back to sites'}
    </button>
  );
  if (activeSite) {
    return (
      <>
        <SiteDetail
          key={activeSite.id}
          site={activeSite}
          onBack={closeDetail}
          onOpenRecord={onOpenRecord}
          // The detail page cannot own the dialog: its `site` prop comes from THIS list, so
          // it could not refresh itself after a save. It raises the intent; this view owns
          // the disposition and the reload that closes the loop (DEV-4424).
          onEdit={() => startEdit(activeSite)}
          // Same contract for the lifecycle (DEV-4424 d): the detail cannot refresh its own
          // Site row, and a deactivate made there has to be visible in the header the
          // instant it lands — so the confirm, the POST and the reload all live here.
          onDeactivate={() => askLifecycle('deactivate', activeSite)}
          onReactivate={() => askLifecycle('reactivate', activeSite)}
        />
        {siteDialog}
        {lifecycleDialog}
      </>
    );
  }
  if (activeSiteId && state.kind === 'loading') {
    return (
      <>
        {backControl}
        <p className="muted" style={{ margin: 0 }}>Loading…</p>
      </>
    );
  }
  if (activeSiteId && state.kind === 'error') {
    // A list LOAD failure is NOT a missing record — surface the error (never a
    // misleading "no longer in your fleet") with a Retry; Back still works.
    return (
      <>
        {backControl}
        <section className="card stack" role="alert">
          <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Couldn’t load this site</h1>
          <p className="muted" style={{ margin: 0 }}>{state.message}</p>
          <button type="button" className="btn-accent" data-surface="accent" onClick={() => void load()} style={{ alignSelf: 'flex-start' }}>
            Retry
          </button>
        </section>
      </>
    );
  }
  // A drill target absent from the loaded list (e.g. a stale deep-link): a graceful
  // not-found state within the pane — Back still works, never a crash (Story 4.4).
  if (activeSiteId && state.kind === 'ready') {
    return (
      <>
        {backControl}
        <section className="card stack">
          <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Site not found</h1>
          <p className="muted" style={{ margin: 0 }}>
            This Site is no longer in your fleet. It may have been removed.
          </p>
        </section>
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Sites</h1>
        {/* The trigger is named "Add a Site" while the dialog's submit stays "Add Site",
            so the two are never ambiguous to a screen reader or to a by-name query. */}
        <button
          type="button"
          className="btn-accent"
          data-surface="accent"
          onClick={() => {
            // Leave any edit disposition first, so the shared dialog opens EMPTY rather
            // than carrying the last-edited Site's values into a create.
            cancelEdit();
            setAdding(true);
          }}
        >
          Add a Site
        </button>
      </div>

      {siteDialog}
      {lifecycleDialog}

      <section className="card stack" aria-labelledby="sites-heading">
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}
        >
          {/* `tabIndex={-1}`: not tab-reachable, but focusable programmatically — the
              landing spot when a renamed row has left the view (see the focus effect). */}
          <h2 id="sites-heading" tabIndex={-1} style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Fleet
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div role="group" aria-label="Filter by type" style={{ display: 'flex', gap: 8 }}>
              {chip('all', 'All')}
              {/* The Ships filter is a maritime-only affordance (Story 3.2, FR-15):
                  a non-maritime Company hides it rather than showing a dead "Ships 0"
                  chip — consistent with the create form's hidden ship option. */}
              {companyMaritime && chip('ship', 'Ships')}
              {chip('office', 'Offices')}
            </div>
            {/* Page-scoped search (Story 4.4): mirrors the Vendors/Devices lists — a
                type="search" input over the Site name, combined with the type chips
                and cleared in one action. */}
            <input
              type="search"
              aria-label="Search sites by name"
              placeholder="Search by name…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowAll(false);
              }}
              style={{
                padding: '6px 10px',
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--rounded-md)',
                color: 'var(--text)',
                fontFamily: 'var(--font-sans)',
                fontSize: 14,
              }}
            />
            {/* The lifecycle toggle (DEV-4424 d). A checkbox rather than a fourth chip:
                the chips are a one-of-three type filter (`role="group"`, `aria-pressed`),
                and this is an orthogonal on/off that COMBINES with whichever one is
                active — putting it in that group would read as a fourth mutually
                exclusive type. Flipping it resets the `PAGE_SIZE` window exactly as the
                chips and the search do, since the row set underneath it just changed. */}
            <label
              className="stack"
              style={{ flexDirection: 'row', gap: 6, alignItems: 'center', fontSize: 13 }}
            >
              <input
                type="checkbox"
                checked={showDeactivated}
                onChange={(e) => {
                  setShowDeactivated(e.target.checked);
                  setShowAll(false);
                }}
              />
              Show deactivated
            </label>
            {hasActiveFilter && (
              <button type="button" className="icon-button" onClick={clearFilters}>
                Clear
              </button>
            )}
          </div>
        </div>

        {state.kind === 'loading' && <p className="muted" style={{ margin: 0 }}>Loading…</p>}

        {state.kind === 'error' && <p className="muted" style={{ margin: 0 }}>{state.message}</p>}

        {state.kind === 'ready' && sites.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>No sites yet — add one above.</p>
        )}

        {/* The fleet is not empty — it is entirely mothballed (DEV-4424 d). Falling through
            to "No sites match your filter or search." would blame a filter the Manager has
            not set, and "No sites yet — add one above." would state something false about a
            Company that has Sites. Named its own state, with the way to see them. */}
        {state.kind === 'ready' && sites.length > 0 && inScope.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>
            Every site in your fleet is deactivated — turn on “Show deactivated” to see them.
          </p>
        )}

        {state.kind === 'ready' && inScope.length > 0 && filtered.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>No sites match your filter or search.</p>
        )}

        {state.kind === 'ready' && filtered.length > 0 && (
          <>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Details</th>
                    <th>Status</th>
                    {/* Right-aligned to sit over the Edit cell, per the sibling actions
                        header at `CuratedDevicesView.tsx:251`. */}
                    <th data-align="right" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((s) => (
                    <tr key={s.id} onClick={() => openSite(s.id)} data-clickable="true">
                      <td>
                        {/* `.cell-cluster` so the pill may wrap below a long vessel name
                            rather than forcing the column's min-content width (the
                            SiteDetail devices-table precedent). */}
                        <span className="cell-cluster">
                          <button
                            type="button"
                            className="cell-link"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              openSite(s.id);
                            }}
                          >
                            {s.name}
                          </button>
                          {/* On the NAME, not in the Status column: Status is the
                              compliance rollup, and a deactivated Site legitimately reads
                              `Not assessed` there — this is the pill that says why.
                              A sibling of the drill button, never its child, or
                              "Deactivated" would join the button's accessible name and
                              `MV Aurora` would stop being an exact match. */}
                          {s.deactivatedAt && <LifecyclePill />}
                        </span>
                      </td>
                      <td style={{ textTransform: 'capitalize' }}>{s.type}</td>
                      <td>
                        {s.type === 'ship' ? <span data-numeric>{s.imo ?? '—'}</span> : (s.city ?? '—')}
                      </td>
                      <td>
                        <SiteStatusCell
                          siteId={s.id}
                          state={complianceState}
                          deactivated={Boolean(s.deactivatedAt)}
                        />
                      </td>
                      <td data-align="right">
                        {/* The ROW is clickable (it opens the detail), so EVERY control
                            here has to stop the event — otherwise it would also drill in
                            and the dialog would open behind the detail page. */}
                        <span className="row-actions">
                          {/* Edit is absent on a deactivated Site, not merely inert: the
                              API refuses a PATCH on one with 409, so the dialog could only
                              ever end in a server error. Reactivate is the control that
                              makes it editable again, and it is right beside it. */}
                          {!s.deactivatedAt && (
                            <button
                              type="button"
                              className="icon-button"
                              // The post-save focus effect finds this control by Site id
                              // rather than by name — a rename changes the name, which is
                              // exactly the case that needs the focus put back.
                              data-edit-site={s.id}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                startEdit(s);
                              }}
                              aria-label={`Edit ${s.name}`}
                            >
                              Edit
                            </button>
                          )}
                          {/* Named for its Site, so the row trigger and the dialog's
                              "Deactivate site" confirm are never ambiguous to a screen
                              reader or a by-name query. */}
                          {s.deactivatedAt ? (
                            <button
                              type="button"
                              className="icon-button"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                askLifecycle('reactivate', s);
                              }}
                              aria-label={`Reactivate ${s.name}`}
                            >
                              Reactivate
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="icon-button"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                askLifecycle('deactivate', s);
                              }}
                              aria-label={`Deactivate ${s.name}`}
                            >
                              Deactivate
                            </button>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtered.length > PAGE_SIZE && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span className="muted" style={{ fontSize: 13 }}>
                  Showing <span data-numeric>{visible.length}</span> of{' '}
                  <span data-numeric>{filtered.length}</span>
                </span>
                {!showAll && (
                  <button type="button" className="icon-button" onClick={() => setShowAll(true)}>
                    Show all
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
